import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject } from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AccessPlan } from '../../../core/generated';
import { PlanCardComponent } from './plan-card/plan-card.component';
import { MatDialog } from '@angular/material/dialog';
import { PlanDetailsModalComponent } from './plan-details-modal/plan-details-modal.component';
import { PlanCardSkeletonComponent } from '../shared/skeleton-loaders';

/**
 * UserPlansComponent (Refactored)
 * Displays list of plans with search, filters, and responsive grid
 * Requirements: 7.1, 7.2, 7.3, 7.5, 7.6, 12.1, 12.3, 15.1, 15.2
 */
@Component({
    selector: 'app-user-plans',
    standalone: true,
    imports: [MaterialModule, CommonModule, FormsModule, PlanCardComponent, PlanCardSkeletonComponent],
    templateUrl: './user-plans.component.html',
    styleUrl: './user-plans.component.scss'
})
export class UserPlansComponent implements OnInit, OnChanges {
  @Input() plans: AccessPlan[] = [];
  @Input() isLoading = false; // Requirement 15.1
  @Output() planSelected = new EventEmitter<AccessPlan>();

  private dialog = inject(MatDialog);

  // Filter state (Requirements 7.2, 7.3)
  searchTerm = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';
  filteredPlans: AccessPlan[] = [];
  
  // Skeleton loader count (Requirement 15.2)
  skeletonCount = 6;

  ngOnInit(): void {
    this.applyFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plans']) {
      this.applyFilters();
    }
  }

  /**
   * Apply search and status filters
   * Requirements: 7.2, 7.3
   */
  applyFilters(): void {
    let result = [...this.plans];

    // Filter by search term (Requirement 7.2)
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(term)
      );
    }

    // Filter by status (Requirement 7.3)
    if (this.statusFilter !== 'all') {
      const isActive = this.statusFilter === 'active';
      result = result.filter(p => p.active === isActive);
    }

    this.filteredPlans = result;
  }

  /**
   * Open plan details modal
   * Requirements: 4.1, 13.1
   */
  openPlanDetails(plan: AccessPlan): void {
    const dialogRef = this.dialog.open(PlanDetailsModalComponent, {
      data: { plan },
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'plan-details-modal'
    });

    // Handle modal result (Requirement 13.2)
    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'viewPayments') {
        this.navigateToPayments(result.plan);
      }
    });
  }

  /**
   * Navigate to payments for this plan
   * Requirement: 13.2
   */
  navigateToPayments(plan: AccessPlan): void {
    this.planSelected.emit(plan);
  }

  /**
   * Get empty state icon based on context
   * Requirements: 12.1, 12.3
   */
  getEmptyStateIcon(): string {
    if (this.plans.length === 0) {
      return 'assignment_late'; // No plans at all
    }
    return 'search_off'; // No search results
  }

  /**
   * Get empty state title based on context
   * Requirements: 12.1, 12.3
   */
  getEmptyStateTitle(): string {
    if (this.plans.length === 0) {
      return 'Nenhum plano cadastrado';
    }
    return 'Nenhum resultado encontrado';
  }

  /**
   * Get empty state message based on context
   * Requirements: 12.1, 12.3
   */
  getEmptyStateMessage(): string {
    if (this.plans.length === 0) {
      return 'Você ainda não possui planos cadastrados. Entre em contato com o suporte para criar seu primeiro plano.';
    }
    if (this.searchTerm && this.statusFilter !== 'all') {
      return `Nenhum plano encontrado com o termo "${this.searchTerm}" e status "${this.getStatusFilterLabel()}".`;
    }
    if (this.searchTerm) {
      return `Nenhum plano encontrado com o termo "${this.searchTerm}". Tente usar palavras-chave diferentes.`;
    }
    if (this.statusFilter !== 'all') {
      return `Nenhum plano ${this.getStatusFilterLabel().toLowerCase()} encontrado.`;
    }
    return 'Nenhum plano encontrado com os filtros aplicados. Tente ajustar os filtros.';
  }

  /**
   * Get status filter label for display
   */
  private getStatusFilterLabel(): string {
    const labels: { [key: string]: string } = {
      'all': 'Todos',
      'active': 'Ativo',
      'inactive': 'Inativo'
    };
    return labels[this.statusFilter] || 'Todos';
  }

  /**
   * Check if empty state should show action button
   * Requirements: 12.4
   */
  shouldShowEmptyStateAction(): boolean {
    // Show action button only when there are plans but filters don't match
    return this.plans.length > 0 && this.filteredPlans.length === 0;
  }

  /**
   * Get empty state action label
   * Requirements: 12.4
   */
  getEmptyStateActionLabel(): string {
    return 'Limpar Filtros';
  }

  /**
   * Get empty state action icon
   * Requirements: 12.4
   */
  getEmptyStateActionIcon(): string {
    return 'clear_all';
  }

  /**
   * Handle empty state action (clear filters)
   * Requirements: 12.4
   */
  handleEmptyStateAction(): void {
    this.searchTerm = '';
    this.statusFilter = 'all';
    this.applyFilters();
  }

  /**
   * Get array for skeleton loaders
   * Requirement 15.2
   */
  getSkeletonArray(): number[] {
    return Array(this.skeletonCount).fill(0);
  }
}
